import { useEffect, useState } from 'react';
import type { TagRule } from '@mlc/shared';
import { deleteTagRule, listTagRules, updateTagRule } from './api';
import { TagRuleEditor } from './TagRuleEditor';
import { Banner, Button, Loading, TextInput } from './ui';

/**
 * Réglage des étiquettes calculées (US-61).
 *
 * ### Ce que cet écran remplace
 *
 * Une phrase : « Elles se mettent à jour toutes seules et **ne peuvent pas être
 * modifiées**. » Elle décrivait fidèlement le code — libellés et seuils étaient
 * des constantes de `computeAutoTags.ts` — et c'était le problème : « moins de
 * 30 min » n'est pas une vérité universelle, et « à refaire » à partir de 4 sur
 * 5 est un choix, celui de personne en particulier.
 *
 * ### Trois gestes, et pas un de plus
 *
 * - **allumer ou éteindre** une règle. Éteinte, l'étiquette disparaît des
 *   fiches, des filtres et des compteurs ; rallumée, elle revient à l'identique.
 *   Une règle d'origine ne se supprime pas : elle s'éteint ;
 * - **renommer** l'étiquette ;
 * - **régler** le seuil, là où la règle en a un.
 *
 * Composer une règle nouvelle est le sujet d'US-62 : ici on règle ce qui
 * existe. La distinction n'est pas cosmétique — un vocabulaire de critères
 * ouvert au moment où l'on ne sait pas encore ce qu'il doit contenir devient un
 * langage de requêtes, ce que le §1 proscrit.
 *
 * ### Ce qui coûte cher, et qu'il faut dire
 *
 * Chaque enregistrement **recalcule toute la bibliothèque** : changer un seuil
 * ne touche aucune recette en particulier et les concerne toutes. L'écran
 * l'annonce, plutôt que de laisser croire à un réglage d'affichage.
 */

/** Les seuils réglables, avec la phrase qui les entoure. Une table plutôt qu'un `if` par règle. */
const SEUILS: Record<string, { avant: string; apres: string; min: number; max: number }> = {
  temps_total_max: { avant: 'Préparation et cuisson au total, au plus', apres: 'minutes', min: 1, max: 600 },
  note_derniere_min: { avant: 'Dernière note au moins', apres: 'sur 5', min: 1, max: 5 },
};

export function TagRules(): React.JSX.Element {
  const [regles, setRegles] = useState<TagRule[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [brouillons, setBrouillons] = useState<Record<string, string>>({});

  useEffect(() => {
    const controller = new AbortController();
    listTagRules(controller.signal)
      .then(setRegles)
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setErreur(e.message);
      });
    return () => { controller.abort(); };
  }, []);

  function supprimer(regle: TagRule): void {
    setEnCours(regle.id);
    setErreur(null);
    deleteTagRule(regle.id)
      .then(() => { setRegles((actuelles) => actuelles?.filter((r) => r.id !== regle.id) ?? null); })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(null); });
  }

  function enregistrer(regle: TagRule, patch: Parameters<typeof updateTagRule>[1]): void {
    setEnCours(regle.id);
    setErreur(null);
    updateTagRule(regle.id, patch)
      .then((modifiee) => {
        setRegles((actuelles) => actuelles?.map((r) => (r.id === modifiee.id ? modifiee : r)) ?? null);
      })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(null); });
  }

  if (regles === null) return <Loading label="les règles d’étiquette" lines={2} />;

  return (
    <div data-testid="tag-rules" className="flex flex-col gap-4">
      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}

      <p className="text-sm text-foreground-secondary">
        Ces étiquettes se posent toutes seules sur vos recettes. Vous décidez de ce qu’elles veulent dire chez vous.
        Chaque changement passe sur toute la bibliothèque.
      </p>

      <ul className="flex flex-col gap-4">
        {regles.map((regle) => {
          const cleSeuil = Object.keys(SEUILS).find((cle) => cle in regle.criteres);
          const seuil = cleSeuil === undefined ? null : (SEUILS[cleSeuil] ?? null);
          const valeur = cleSeuil === undefined
            ? ''
            : String((regle.criteres as Record<string, number | boolean | undefined>)[cleSeuil] ?? '');
          const brouillon = brouillons[regle.id] ?? regle.label;

          return (
            <li key={regle.id} className="flex flex-col gap-2 border-t border-border pt-4 first:border-t-0 first:pt-0">
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor={`regle-nom-${regle.id}`} className="sr-only">
                  Nom de l’étiquette {regle.label}
                </label>
                <TextInput
                  id={`regle-nom-${regle.id}`}
                  value={brouillon}
                  disabled={enCours === regle.id}
                  onChange={(e) => { setBrouillons((b) => ({ ...b, [regle.id]: e.target.value })); }}
                  onBlur={() => {
                    const propre = brouillon.trim();
                    if (propre !== '' && propre !== regle.label) enregistrer(regle, { label: propre });
                  }}
                  className="w-56"
                />
                <span className="flex-1" />
                {/* Deux états, deux boutons : une case à cocher demanderait de
                    deviner si elle allume ou éteint (US-57, même règle que les
                    unités). */}
                <Button
                  type="button"
                  variant={regle.enabled ? 'primary' : 'secondary'}
                  size="compact"
                  aria-pressed={regle.enabled}
                  disabled={enCours === regle.id}
                  onClick={() => { enregistrer(regle, { enabled: !regle.enabled }); }}
                >
                  {regle.enabled ? 'Active' : 'Éteinte'}
                </Button>
                {/* Une règle **d'origine** ne se supprime pas : elle s'éteint
                    (US-61). Les effacer laisserait une application dont on ne
                    peut plus retrouver le comportement de départ. */}
                {regle.builtin_key === null && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="compact"
                    disabled={enCours === regle.id}
                    onClick={() => { supprimer(regle); }}
                  >
                    Supprimer
                  </Button>
                )}
              </div>

              {seuil !== null && cleSeuil !== undefined && (
                <div className="flex flex-wrap items-center gap-2 text-sm text-foreground-secondary">
                  <span>{seuil.avant}</span>
                  <label htmlFor={`regle-seuil-${regle.id}`} className="sr-only">
                    Seuil de l’étiquette {regle.label}
                  </label>
                  <TextInput
                    id={`regle-seuil-${regle.id}`}
                    type="number"
                    inputMode="numeric"
                    min={seuil.min}
                    max={seuil.max}
                    defaultValue={valeur}
                    disabled={enCours === regle.id}
                    onBlur={(e) => {
                      const nombre = Number(e.target.value);
                      if (!Number.isFinite(nombre) || nombre < seuil.min || nombre > seuil.max) return;
                      if (String(nombre) === valeur) return;
                      enregistrer(regle, { criteres: { [cleSeuil]: nombre } });
                    }}
                    className="w-20"
                  />
                  <span>{seuil.apres}</span>
                </div>
              )}

              <p className="text-meta">
                {regle.scope === 'journal'
                  ? 'Calculée sur votre journal : chacun voit la sienne.'
                  : 'Décrit la recette : la même pour tout le monde.'}
              </p>
            </li>
          );
        })}
      </ul>

      {/* Composer une règle nouvelle (US-62) : le vocabulaire est fermé, les
          critères se cumulent par ET, et la portée se déduit de ce qu'ils
          regardent. */}
      <TagRuleEditor onCreated={(creee) => { setRegles((actuelles) => [...(actuelles ?? []), creee]); }} />
    </div>
  );
}
